'use client';

import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Button } from 'antd';
import callApi from '../../Api/config';
import { getAdminName } from '../../service/CookieManager';
import { toastError, toastSuccess } from '../../service/Toast';

interface ReviewPaymentModalProps {
  visible: boolean;
  paymentId: string;
  orderAmount: number;
  onClose: () => void;
  onSuccess: () => void;
}

type ReviewAction = 'paid' | 'payment_unsuccessful' | 'partial';

const REVIEW_OPTIONS: Array<{
  value: ReviewAction;
  kicker: string;
  title: string;
  hint: string;
}> = [
  {
    value: 'paid',
    kicker: 'Option A',
    title: 'Payment Confirmed — Paid',
    hint: 'User end status: Paid',
  },
  {
    value: 'payment_unsuccessful',
    kicker: 'Option B',
    title: 'Payment Not Received',
    hint: 'User end status: Payment Unsuccessful',
  },
  {
    value: 'partial',
    kicker: 'Option C',
    title: 'Partial Payment Received',
    hint: 'User end status: Partial Payment Received',
  },
];

const ReviewPaymentModal: React.FC<ReviewPaymentModalProps> = ({
  visible,
  paymentId,
  orderAmount,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const cmsUser = getAdminName() || 'CMS User';
  const [selectedAction, setSelectedAction] = useState<ReviewAction>('paid');

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payload: any = {
        paymentId,
        status: selectedAction,
        remarks: values.remarks || '',
        confirmedBy: cmsUser,
        accountName: values.accountName || '',
        netReceived: values.netReceived ?? 0,
        receivedAmount: values.receivedAmount ?? 0,
      };

      const res: any = await callApi('/partner-portal/admin/payments/verify', 'POST', payload);

      if (res?.status || res?.data?.isSuccess === 1) {
        toastSuccess(res?.data?.message || 'Payment review updated successfully');
        form.resetFields();
        setSelectedAction('paid');
        onClose();
        onSuccess();
      } else {
        toastError(res?.data?.message || res?.message || 'Failed to update payment review');
      }
    } catch (error) {
      console.error('Review error:', error);
      toastError('An error occurred while updating payment review');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedAction('paid');
    onClose();
  };

  const selectedMeta = REVIEW_OPTIONS.find(option => option.value === selectedAction);

  const optionButtonStyle = (active: boolean): React.CSSProperties => ({
    width: '100%',
    textAlign: 'left',
    background: active ? '#f0f4f7' : '#fff',
    border: `1px solid ${active ? '#637f92' : '#e6edf2'}`,
    borderRadius: 6,
    padding: '12px 14px',
    cursor: 'pointer',
  });

  return (
    <Modal
      title="Review Payment Request"
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={860}
      destroyOnClose
    >
      <div
        style={{
          marginBottom: 16,
          padding: '10px 16px',
          background: '#f0f4f7',
          borderRadius: 4,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ color: '#637f92', fontWeight: 700 }}>Order Total</span>
        <span style={{ fontWeight: 700, color: '#2c3e50' }}>${orderAmount.toFixed(2)}</span>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ reviewAction: 'paid', netReceived: orderAmount }}
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
          <div
            style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            {REVIEW_OPTIONS.map(option => {
              const active = selectedAction === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  style={optionButtonStyle(active)}
                  onClick={() => {
                    setSelectedAction(option.value);
                    form.setFieldsValue({ reviewAction: option.value });
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#8aa0ad',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}
                  >
                    {option.kicker}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#2c3e50' }}>
                    {option.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#637f92' }}>{option.hint}</div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              flex: 1,
              border: '1px solid #e6edf2',
              borderRadius: 6,
              background: '#fafcfd',
              padding: 18,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: '#637f92', marginBottom: 4 }}>
              {selectedMeta?.title}
            </div>
            <div style={{ fontSize: 12, color: '#8aa0ad', marginBottom: 16 }}>
              {selectedMeta?.hint}
            </div>

            {selectedAction === 'paid' && (
              <>
                <Form.Item
                  name="netReceived"
                  label="Net Received ($)"
                  rules={[{ required: true, message: 'Please enter net received amount' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    precision={2}
                    min={0}
                    placeholder="Enter net received amount"
                  />
                </Form.Item>
                <Form.Item
                  name="accountName"
                  label="Account Name"
                  extra="For internal team use"
                  rules={[{ required: true, message: 'Please enter account name' }]}
                >
                  <Input placeholder="Enter account name" />
                </Form.Item>
                <Form.Item label="Confirmed By">
                  <Input value={cmsUser} disabled />
                </Form.Item>
              </>
            )}

            {selectedAction === 'payment_unsuccessful' && (
              <Form.Item
                name="remarks"
                label="Remark"
                rules={[{ required: true, message: 'Please enter a remark' }]}
              >
                <Input.TextArea rows={5} placeholder="Enter reason payment was not received" />
              </Form.Item>
            )}

            {selectedAction === 'partial' && (
              <>
                <Form.Item
                  name="receivedAmount"
                  label="Received Amount ($)"
                  rules={[
                    { required: true, message: 'Please enter received amount' },
                    {
                      validator: (_, value) => {
                        if (value == null) return Promise.resolve();
                        if (Number(value) <= 0) {
                          return Promise.reject(new Error('Amount must be greater than 0'));
                        }
                        if (orderAmount > 0 && Number(value) >= orderAmount) {
                          return Promise.reject(
                            new Error('Use Payment Confirmed if the full amount was received')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    precision={2}
                    min={0}
                    placeholder="Enter amount received"
                  />
                </Form.Item>
                <Form.Item
                  name="remarks"
                  label="Remark"
                  rules={[{ required: true, message: 'Please enter a remark' }]}
                >
                  <Input.TextArea rows={4} placeholder="Enter remarks about the partial payment" />
                </Form.Item>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit Review
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default ReviewPaymentModal;
